import { fromBaseRecord } from '../janus-engine/compile/from-base-context';
import { encounterCreatureField } from './array-types';

type Row = Record<string, unknown>;
type Preview = { ids: string[]; clear(announce: boolean): void };
type PreviewState = { active: Map<string, Preview>; closed: Set<string> };
export interface CreatureSpawnPreviewFrame {
    type: 'janus_spawn_preview'; encounter_id: string; actor_id: string; operation_id: string; rows: Row[];
}
declare global { interface Window { ftCreatureSpawnPreviewState?: WeakMap<Row, PreviewState> } }

function stateFor(encounter: Row): PreviewState {
    const states = window.ftCreatureSpawnPreviewState ??= new WeakMap<Row, PreviewState>();
    let state = states.get(encounter);
    if (!state) { state = { active: new Map(), closed: new Set() }; states.set(encounter, state); }
    return state;
}

function heldEncounter(encounterId: string): Row | null {
    const cache = (window as unknown as { FateTide?: { encounterCache?: Map<string, unknown> } }).FateTide?.encounterCache;
    const encounter = cache?.get(encounterId.toUpperCase());
    return fromBaseRecord(encounter) ? encounter : null;
}

/** Display-only identities borrow held definitions. No base adoption, compile or save. */
export function stageCreatureSpawnPreview(encounterId: string, encounter: Row, operation: string, rows: Row[]): Preview {
    const state = stateFor(encounter), prior = state.active.get(operation);
    if (prior) return prior;
    if (state.closed.has(operation)) return { ids: [], clear() {} };
    const envelope = fromBaseRecord(encounter._entity_base) ? encounter._entity_base : null;
    const base = envelope && fromBaseRecord(envelope.base) ? envelope.base : null;
    const catalog = fromBaseRecord(base?.catalog) ? base.catalog : fromBaseRecord(encounter.catalog) ? encounter.catalog : {};
    const staged = new Set<Row>(rows.map(row => {
        const id = String(encounterCreatureField(row, 'encounter_creature_id'));
        const itemId = String(encounterCreatureField(row, 'item_id'));
        const selected = catalog[itemId.toUpperCase()], definition = fromBaseRecord(selected) ? selected : {};
        return { entity_id: id, items: definition.items ?? { item_id: itemId, item_type: 'creature' }, encounter_creatures: row,
            position: Object.fromEntries(['x_pos', 'y_pos', 'z_pos'].map(key => [key, encounterCreatureField(row, key) ?? 0])),
            inventory: [], _entity_refusal: 'unported:creature-base-pending' };
    }));
    const ids = [...staged].map(row => String(row.entity_id));
    const announce = (): void => {
        window.dispatchEvent(new CustomEvent('ft:entity-base-compiled', { detail: { entityId: encounterId, entity: envelope?.entity ?? 'encounter',
            baseRev: envelope?.base_rev, computed: encounter, applied: true } }));
        window.dispatchEvent(new CustomEvent('ft:inventory-base-compiled', { detail: { entityId: encounterId, inventoryUnchanged: true } }));
    };
    const preview: Preview = { ids, clear(notify) {
        clearTimeout(expiry);
        state.active.delete(operation); state.closed.add(operation);
        if (state.closed.size > 512) state.closed.delete(state.closed.values().next().value!);
        if (!staged.size) return;
        encounter.encounter_creatures_arr = (encounter.encounter_creatures_arr as Row[]).filter(row => !staged.has(row));
        staged.clear();
        if (notify) announce();
    } };
    const expiry = setTimeout(() => preview.clear(true), 30_000);
    state.active.set(operation, preview);
    encounter.encounter_creatures_arr = [...Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [], ...staged];
    window.dispatchEvent(new CustomEvent('ft:spawn-preview-applied', { detail: { operation_id: operation, encounter_id: encounterId, creatureIds: ids } }));
    announce();
    return preview;
}

export function clearCreatureSpawnPreview(encounterId: string, operation: string, announce = false): void {
    const encounter = heldEncounter(encounterId);
    if (!encounter) return;
    const state = stateFor(encounter);
    state.active.get(operation)?.clear(announce);
    state.closed.add(operation);
    if (state.closed.size > 512) state.closed.delete(state.closed.values().next().value!);
}

export function handleCreatureSpawnPreview(frame: CreatureSpawnPreviewFrame): void {
    const encounter = heldEncounter(frame.encounter_id);
    if (!encounter) return;
    if (!frame.rows.length) clearCreatureSpawnPreview(frame.encounter_id, frame.operation_id, true);
    else stageCreatureSpawnPreview(frame.encounter_id, encounter, frame.operation_id, frame.rows);
}

// Both the local publisher and the peer router emit this before adopting final birth rows.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') window.addEventListener('ft:truth-frame-applied', event => {
    const frame = (event as CustomEvent).detail;
    if (frame?.encounter_id && frame?.operation_id) clearCreatureSpawnPreview(frame.encounter_id, frame.operation_id);
});
